import { countries } from "@redotech/locale/countries";
import { sharedEmailSchemaFields } from "@redotech/redo-flows-core-schema-common/shared-email-schema-fields";
import { AdvancedFlowTokenType } from "@redotech/redo-flows-core-schema-common/type-system/data-types/base-types";
import {
  SchemaInstance,
  defineSchema,
} from "@redotech/redo-flows-core-schema-common/type-system/schema";
import {
  MOCK_CUSTOM_FIELD,
  MOCK_PRODUCT_IMAGE_URL,
} from "@redotech/redo-marketing-mock-common/mock-values";
import { Provider } from "@redotech/redo-order-common/provider";
import {
  TrackableType,
  trackingStatuses,
} from "@redotech/redo-order-tracking-trackable-common/trackable";
import { Trackable } from "@redotech/redo-order-tracking-trackable-return-common/trackable";
import { TrackingPlatform } from "@redotech/redo-order-tracking-tracker-common/tracking-platform";
import { ReviewPrioritizationOption } from "@redotech/redo-reviews-common/review-prioritization-option";
import { subscriptionUpsellSchemaFields } from "../subscription-upsell-fields";

const exampleTrackable: Trackable = {
  type: TrackableType.ORDER,
  id: "1234567890",
  customer: { email: "example@example.com" },
  fulfillments: [
    {
      id: "fulfillment-123",
      trackers: [
        {
          trackingPlatform: TrackingPlatform.EASYPOST,
          id: "tracker-123",
          tracking_code: "1Z999AA10123456784",
          status: "in_transit",
          status_detail: "arrived_at_facility",
          carrier: "UPS",
          tracking_details: [
            {
              message: "Package received by carrier",
              status: "pre_transit",
              datetime: "2024-01-15T08:00:00Z",
              tracking_location: {
                city: "Los Angeles",
                state: "CA",
                country: "US",
                zip: "90001",
              },
            },
            {
              message: "In transit to destination",
              status: "in_transit",
              datetime: "2024-01-15T12:00:00Z",
              tracking_location: {
                city: "Phoenix",
                state: "AZ",
                country: "US",
                zip: "85001",
              },
            },
          ],
          public_url: "https://track.example.com/tracker123",
          est_delivery_date: "2024-01-20T00:00:00Z",
          updated_at: "2024-01-15T12:00:00Z",
        },
      ],
      lineItems: [],
      isShipped: true,
      status: "in_transit",
      tracking_numbers: ["1Z999AA10123456784"],
      redoTrackingPageUrl:
        "https://example.com/account?order_tracking_token=example&order_tracking_fulfillment_index=0&trackable_type=order",
    },
    {
      id: "fulfillment-456",
      trackers: [
        {
          trackingPlatform: TrackingPlatform.EASYPOST,
          id: "tracker-456",
          tracking_code: "9400111899223344556677",
          status: "pre_transit",
          status_detail: "status_update",
          carrier: "USPS",
          tracking_details: [
            {
              message: "Shipping label created",
              status: "pre_transit",
              datetime: "2024-01-16T10:00:00Z",
              tracking_location: {
                city: "San Francisco",
                state: "CA",
                country: "US",
                zip: "94102",
              },
            },
          ],
          public_url: "https://track.example.com/tracker456",
          est_delivery_date: "2024-01-22T00:00:00Z",
          updated_at: "2024-01-16T10:00:00Z",
        },
      ],
      lineItems: [],
      isShipped: false,
      status: "pre_transit",
      tracking_numbers: ["9400111899223344556677"],
      redoTrackingPageUrl:
        "https://example.com/account?order_tracking_token=example&order_tracking_fulfillment_index=1&trackable_type=order",
    },
  ],
  lineItems: [],
  currentEmailFlows: [],
  createdAt: new Date(),
  teamId: "team-id",
  originalOrder: {
    id: "1234567890",
    landedCost: null,
    shopify: {
      id: "1234567890",
      number: 1001,
      name: "#1001",
      fulfillments: [],
      lineItems: [],
      createdAt: new Date(),
      contact_email: "contact@example.com",
      customer: { id: "1234567890" },
      shippingLines: [],
      priceBeforeDiscounts: 100,
      discounts: { individualDiscounts: [] },
      presentmentCurrency: "USD",
      currentSubtotalPriceSet: { presentment_money: { amount: 100 } },
      currentTotalPriceSet: { presentment_money: { amount: 100 } },
      currentTotalTaxSet: { presentment_money: { amount: 100 } },
      tags: [],
    },
  },
};

export const baseTrackingSchema = defineSchema({
  fields: {
    orderNumber: {
      dataType: "Text",
      documentation: "The shopify order number",
    },
    poNumber: {
      dataType: "Maybe Text",
      documentation: "The purchase order number (for B2B orders)",
    },
    externalOrderId: {
      dataType: "Text",
      documentation: "The external order ID from the ecommerce platform",
    },
    redoId: {
      dataType: "Text",
      documentation: "The unique identifier for the trackable item in Redo",
    },
    currentDate: { dataType: "Date", documentation: "The current date" },
    trackableType: {
      dataType: "Enum",
      documentation: 'The type of trackable item: "order" or "return"',
      values: ["order", "return"],
    },
    // order-specific name for backwards compatibility
    orderSummary: {
      dataType: "Trackable Summary",
      documentation: "The summary of the order or return",
    },
    reviewRequest: {
      dataType: "Review Request",
      documentation: "A review request for the current order items",
    },
    customerCountry: {
      dataType: "Enum",
      documentation: "Which country the customer is in",
      values: countries.map((country) => country.name),
    },
    productTags: {
      dataType: "Multiple Text",
      documentation: "The tags of all the products in the order",
    },
    redoCustomerId: {
      dataType: "Text",
      documentation: "The Redo customer ID.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },

    // TODO: make this work
    // productProperties: {
    //   dataType: "Multiple Text",
    //   documentation:
    //     "True if any products in the order has any of these properties",
    // },
    deliveryMethod: {
      dataType: "Multiple Text",
      documentation: "The delivery method of the shipment",
    },
    deliveryStatus: {
      dataType: "Enum",
      values: trackingStatuses.filter((s) => s !== "unknown"),
      documentation: "The delivery status of the shipment.",
    },
    productNames: {
      dataType: "Multiple Text",
      documentation: "The names of the products in the order",
    },
    skus: {
      dataType: "Multiple Text",
      documentation: "The SKUs of the products in the order",
    },
    orderTags: {
      dataType: "Multiple Text",
      documentation: "The tags of the order",
    },
    fulfillmentId: {
      dataType: "Maybe Text",
      documentation: "The fulfillment ID of the order",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    fulfillmentLocation: {
      dataType: "Token",
      tokenType: AdvancedFlowTokenType.FULFILLMENT_LOCATION_ID,
      documentation:
        "The fulfillment location the shipment originated from. Empty for triggers that fire before a fulfillment exists (e.g. order_created).",
    },
    totalPrice: {
      dataType: "Number",
      documentation: "The total price of the order (or items being returned)",
    },
    packageNumberDescription: {
      dataType: "Maybe Text",
      documentation:
        "Text that specifies which fulfillment received a tracking update, if applicable",
    },
    packageNumber: {
      dataType: "Maybe Number",
      documentation:
        "The 1-based number of the fulfillment that triggered this flow among those requiring shipping (e.g. 1 for the first package). Empty when no shipping fulfillment is relevant (e.g. order_created).",
    },
    isSplitShipment: {
      dataType: "Boolean",
      label: "Split shipment",
      documentation:
        "True when the order has multiple tracking codes, or has at least one shipped tracking code and additional unfulfilled items that require physical shipping.",
    },
    relevantLineItems: {
      dataType: "Multiple Line Item",
      documentation: "The line items relevant to the shipment",
    },
    otherLineItems: {
      dataType: "Maybe Multiple Line Item",
      documentation: "Other line items",
    },

    unsubscribeLink: {
      dataType: "Maybe Url",
      documentation:
        "The link to unsubscribe from email updates. Only displays when the customer is subscribed",
    },
    oneClickUnsubscribeLink: {
      dataType: "Maybe Url",
      documentation:
        "The link to unsubscribe from email updates with one click for email delivery compliance. Only displays when the customer is subscribed",
      hideFromInsertionModal: true,
    },
    subscribeLink: {
      dataType: "Maybe Url",
      documentation:
        "The link to subscribe to email updates. Only displays when the customer is not subscribed",
    },
    trackingLink: {
      dataType: "Url",
      documentation:
        "The Redo tracking page URL for the fulfillment that triggered this flow. For the order-level URL, use redoTrackingPageUrl.",
    },
    redoTrackingPageUrl: {
      dataType: "Url",
      documentation:
        "The Redo-hosted tracking page URL for the entire order. Per-fulfillment URLs are available on each fulfillment object.",
    },
    orderDetailsLink: {
      dataType: "Url",
      documentation: "The link to view the order details",
    },
    customerPortalLink: {
      dataType: "Maybe Url",
      documentation: "Authenticated link to the customer portal order page",
    },
    orderEditingLink: {
      dataType: "Maybe Url",
      documentation: "Authenticated deep link to the order editing page",
    },
    shopifyOrderDetailsLink: {
      dataType: "Maybe Url",
      documentation: "The link to view the order details in Shopify",
    },
    carrierLink: {
      dataType: "Maybe Url",
      documentation: "The link to view the carrier details",
    },
    carrier: {
      dataType: "Maybe Text",
      documentation: "The carrier for the shipment (e.g., USPS, UPS, FedEx)",
    },
    tracker: {
      dataType: "Tracker",
      documentation: "The tracker for the shipment",
    },
    cartContext: {
      dataType: "Cart Context",
      documentation:
        "Data related to the order or return that is useful for generating product recommendations for an interactive cart",
    },
    customerFullName: {
      dataType: "Maybe Text",
      documentation: "The full name of the customer",
      hideFromConditionStep: true,
    },
    customerFirstName: {
      dataType: "Maybe Text",
      documentation: "The first name of the customer",
      hideFromConditionStep: true,
    },
    customerLastName: {
      dataType: "Maybe Text",
      documentation: "The last name of the customer",
      hideFromConditionStep: true,
    },
    customerEmailSubscriber: {
      dataType: "Boolean",
      documentation:
        "True if the customer is subscribed to email marketing updates",
    },
    /** @deprecated, use customerSmsMarketingSubscriber and customerSmsTransactionalSubscriber instead. This entire system may be refactored soon btw. */
    customerSmsSubscriber: {
      dataType: "Boolean",
      documentation:
        "True if the customer is subscribed to ether transactional or marketing SMS updates",
    },
    customerSmsMarketingSubscriber: {
      dataType: "Boolean",
      documentation:
        "True if the customer is subscribed to SMS marketing updates",
    },
    customerSmsTransactionalSubscriber: {
      dataType: "Boolean",
      documentation:
        "True if the customer is subscribed to transactional SMS updates",
    },
    customerEmail: {
      dataType: "Email",
      documentation: "The email address of the customer",
    },
    customerPhoneNumber: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer",
    },
    trackingNumber: {
      dataType: "Maybe Text",
      documentation: "The tracking number of the shipment",
    },
    ecommerceProvider: {
      dataType: "Enum",
      values: [Provider.COMMENTSOLD, Provider.SHOPIFY, Provider.COMMERCE_CLOUD],
      documentation: "The ecommerce provider of the trackable item",
    },
    orderSource: {
      dataType: "Maybe Text",
      suggestionType: "orderSource",
      documentation:
        "The source/sales channel where the order originated (e.g., 'web', 'tiktok_channel', 'pos')",
    },
    metafields: {
      dataType: "Object",
      documentation:
        "The metafields of the trackable item (Use `.` to access nested fields)",
    },
    returnSubmitted: {
      dataType: "Boolean",
      documentation: "Whether a return was submitted for any item in the order",
    },
    hasProductsEligibleForReview: {
      dataType: "Boolean",
      documentation:
        "Whether there are products in the order that are eligible for review (i.e., not all products have been returned)",
    },
    leaveReviewLink: {
      dataType: "Maybe Url",
      documentation: "The link to leave a review for the order",
    },
    hasLeftReviewForOrder: {
      dataType: "Boolean",
      documentation: "Whether the customer has left a review for the order",
    },
    teamSupportEmailUrl: {
      dataType: "Maybe Url",
      documentation: "The email address of the team support",
    },
    ...sharedEmailSchemaFields,
    ...subscriptionUpsellSchemaFields,
  },
});

export const exampleBaseTrackingSchemaInstance: SchemaInstance<
  typeof baseTrackingSchema
> = {
  orderNumber: "#1001",
  poNumber: "PO-12345",
  externalOrderId: "1234567890",
  redoId: "65b81f41da22f4000718b34e",
  redoCustomerId: "66b131782f9829bd1ed05bd7",
  fulfillmentId: "0987654321234567890",
  fulfillmentLocation: "gid://shopify/Location/1234567890",
  currentDate: Temporal.Now.instant().toZonedDateTimeISO("UTC").toPlainDate(),
  trackableType: "Order",
  orderSummary: {
    trackable: exampleTrackable,
    trackingPageUrl: "https://example.com",
    trackableType: {
      lowerCase: "order",
      titleCase: "Order",
      verb: "purchased",
    },
    orderDetails: {
      orderDate: new Date(),
      orderId: "1234567890",
      fulfilled: true,
    },
    relevantLineItems: [
      {
        title: "Product 1",
        variantTitle: "Variant 1",
        image: { src: MOCK_PRODUCT_IMAGE_URL },
        price: 10.0,
        isRedo: false,
        requiresShipping: true,
        tags: [],
        properties: { [MOCK_CUSTOM_FIELD]: "Value" },
        sku: "SKU-1",
        quantity: 1,
        id: 1,
        green_return: true,
        priceSet: { presentment_money: { amount: 10.0, currency_code: "USD" } },
        compareAtPrice: "20.00",
      },
    ],
    otherLineItems: [
      {
        title: "Product 2",
        variantTitle: "Variant 2",
        image: { src: MOCK_PRODUCT_IMAGE_URL },
        price: 10.0,
        isRedo: false,
        requiresShipping: true,
        tags: [],
        properties: {},
        sku: "SKU-2",
        quantity: 1,
        id: 2,
        green_return: true,
        priceSet: { presentment_money: { amount: 10.0, currency_code: "USD" } },
      },
    ],
    costSummary: {
      shippingProduct: "Shipping",
      shippingProductCost: "$10.00",
      taxCost: "$2.00",
      totalCost: "$29.00",
      priceBeforeDiscounts: "$20.00",
      discountLines: [{ label: "SAVE10", cost: "-$1.00" }],
      duties: undefined,
    },
    savingsSummary: { fullPrice: "$30.00", totalSavings: "$13.00" },
    customerInformation: {
      shippingAddress: {
        name: "John Doe",
        address1: "123 Main St",
        address2: "Suite 100",
        city: "San Francisco",
        province: "CA",
        postalCode: "94107",
        phone: "+1234567890",
      },
      billingAddress: {
        name: "John Doe",
        address1: "123 Main St",
        address2: "Suite 100",
        city: "San Francisco",
        province: "CA",
        postalCode: "94107",
        phone: "+1234567890",
      },
    },
  },
  reviewRequest: {
    relevantLineItems: [
      {
        product: {
          title: "Product 1",
          variantTitle: "Variant 1",
          image: { src: MOCK_PRODUCT_IMAGE_URL },
          price: 10.0,
          isRedo: false,
          requiresShipping: true,
          tags: [],
          properties: {},
          sku: "SKU-1",
          id: 1,
          green_return: true,
          priceSet: {
            presentment_money: { amount: 10.0, currency_code: "USD" },
          },
        },
        quantity: 1,
      },
    ],
    orderNumber: "#1001",
    storeUrl: "https://example.com",
    reviewPrioritization: ReviewPrioritizationOption.HighestPrice,
    customerEmail: "example@test.com",
    customerReturnedProductIdsForReviewFiltering: [],
  },
  customerEmailSubscriber: true,
  customerSmsSubscriber: false,
  customerSmsMarketingSubscriber: false,
  customerSmsTransactionalSubscriber: false,
  customerCountry: "United States",
  productTags: ["tag1", "tag2"],
  productNames: ["Product 1", "Product 2"],
  skus: ["sku1", "sku2"],
  orderTags: ["tag1", "tag2"],
  packageNumberDescription: "Package 1 of 2",
  packageNumber: 1,
  isSplitShipment: true,
  relevantLineItems: [
    {
      product: {
        id: 1,
        title: "Product 1",
        variantTitle: "Variant 1",
        image: { src: MOCK_PRODUCT_IMAGE_URL },
        price: 10.0,
        priceSet: { presentment_money: { amount: 10.0, currency_code: "USD" } },
        isRedo: false,
        requiresShipping: true,
        tags: [],
        properties: {},
        sku: "SKU-1",
        green_return: true,
      },
      quantity: 1,
    },
  ],
  totalPrice: 10.0,
  otherLineItems: undefined,
  unsubscribeLink: undefined,
  oneClickUnsubscribeLink: undefined,
  subscribeLink: "https://example.com/subscribe",
  subscribableProductInCartName: "Subscribable Product",
  hasSubscribableProduct: true,
  subscriptionCheckoutUrl: "https://example.com/checkout/c/abcdef",
  subscribableLines: [
    {
      variantGid: "gid://shopify/ProductVariant/123",
      sellingPlanGid: "gid://shopify/SellingPlan/456",
      quantity: 1,
    },
  ],
  trackingLink: "https://example.com/track",
  redoTrackingPageUrl:
    "https://example.com/account?order_tracking_token=example&trackable_type=order",
  orderDetailsLink: "https://example.com/order-details",
  customerPortalLink: "https://example.com/customer-portal",
  orderEditingLink: "https://example.com/order-editing",
  shopifyOrderDetailsLink: "https://example.com/shopify-order-details",
  carrierLink: "https://example.com/carrier-link",
  carrier: "USPS",
  tracker: {
    trackableType: {
      lowerCase: "order",
      titleCase: "Order",
      verb: "purchased",
    },
    orderCreatedAt: new Date(),
    code: "1234567890",
    link: "https://example.com",
    deliveryEstimate: new Date(),
    status: "pre_transit",
    updatedAt: new Date(),
    details: [
      {
        message: "Order received",
        status: "pre_transit",
        datetime: new Date(),
        trackingLocation: {
          city: "San Francisco",
          state: "CA",
          country: "US",
          zip: "94107",
        },
      },
    ],
    carrier: "USPS",
    carrierDetail: {
      service: "USPS First-Class Package Service",
      container_type: "envelope",
      est_delivery_date_local: "2025-01-01T00:00:00",
      est_delivery_time_local: "2025-01-01T00:00:00",
      guaranteed_delivery_date: "2025-02-01T00:00:00",
      alternate_identifier: "alternate-identifier",
      initial_delivery_attempt: "2025-01-01T00:00:00",
      origin_location: "San Francisco, CA, US, 94107",
      origin_tracking_location: {
        city: "San Francisco",
        state: "CA",
        country: "US",
        zip: "94107",
        object: "TrackingLocation",
      },
      destination_location: "New York, NY, US, 10001",
      destination_tracking_location: {
        city: "New York",
        state: "NY",
        country: "US",
        zip: "10001",
        object: "TrackingLocation",
      },
      object: "CarrierDetail",
    },
    trackable: exampleTrackable,
  },
  cartContext: {
    productIdsForRecommendations: [],
    alreadyInCart: [],
    existingCartId: null,
  },
  customerFullName: "John Doe",
  customerFirstName: "John",
  customerLastName: "Doe",
  customerEmail: "johndoe@example.com",
  customerPhoneNumber: "+1234567890",
  trackingNumber: "1234567890",
  deliveryMethod: ["Economy Shipping (7-10 Business Days)"],
  deliveryStatus: "pre_transit",
  ecommerceProvider: Provider.SHOPIFY,
  orderSource: "web",
  metafields: { custom_metafield_1: "custom value 1" },
  returnSubmitted: false,
  hasProductsEligibleForReview: true,
  viewInBrowserLink: undefined, // gets populated later
  teamSupportEmailUrl: "mailto:support@example.com",
  leaveReviewLink: "https://example.com/leave-review",
  hasLeftReviewForOrder: false,
};
